<template>
  <div class="cms-page-form">
    <cms-form-header
      :title="isEditing ? $t('cms.pages.editPage') : $t('cms.pages.createPage')"
      :disabled="isSaving || isLoadingPage"
      @back="goBack"
      @save-draft="saveDraft"
      @save-publish="saveAndPublish"
    />

    <cms-form-layout>
      <form @submit.prevent="handleSubmit">
        <cms-form-section :title="$t('cms.pages.generalInfo')">
          <div class="cms-form-grid">
            <cms-input
              v-model="form.custom_url"
              :label="$t('cms.pages.customUrl')"
              :help-text="$t('cms.pages.customUrlHelp')"
              :full-width="true"
            />
            <cms-select
              v-model="form.page_type"
              :label="$t('cms.pages.pageType')"
              :options="pageTypeOptions"
              :placeholder="$t('cms.pages.pageType')"
            />
            <cms-input
              v-model.number="form.order"
              type="number"
              :label="$t('cms.pages.order')"
              :help-text="$t('cms.pages.orderHelp')"
            />
          </div>
        </cms-form-section>

        <cms-form-section :title="$t('cms.pages.translationsSection')">
          <cms-translation-tabs :languages="languages" v-model="currentLanguage">
            <template #default="{ locale }">
              <div class="cms-form-grid">
                <cms-input
                  v-model="getTranslation(locale).title"
                  :label="`${$t('cms.pages.pageTitle')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-editor
                  v-model="getTranslation(locale).content"
                  :label="`${$t('cms.pages.content')} (${locale.toUpperCase()})`"
                />
                <cms-input
                  v-model="getTranslation(locale).seo_title"
                  :label="`${$t('cms.pages.seoTitle')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-textarea
                  v-model="getTranslation(locale).seo_description"
                  :label="`${$t('cms.pages.seoDescription')} (${locale.toUpperCase()})`"
                  :rows="3"
                  :full-width="true"
                  :max-length="140"
                  :show-counter="true"
                />
                <cms-list-input
                  v-model="getTranslation(locale).keywords"
                  :label="`${$t('cms.pages.seoKeywords')} (${locale.toUpperCase()})`"
                  :placeholder="$t('cms.pages.seoKeywordsPlaceholder')"
                  :add-button-text="$t('cms.activities.addKeyword')"
                  :full-width="true"
                />
              </div>
            </template>
          </cms-translation-tabs>
        </cms-form-section>
      </form>

      <template #sidebar>
        <cms-sidebar>
          <cms-sidebar-section :title="$t('cms.pages.publishing')">
            <label class="cms-checkbox-item">
              <input v-model="form.is_public" type="checkbox" class="cms-checkbox" style="margin-right: 8px;" />
              <span>{{ $t('cms.stories.isPublic') }}</span>
            </label>
            <label class="cms-checkbox-item">
              <input v-model="form.is_featured" type="checkbox" class="cms-checkbox" style="margin-right: 8px;" />
              <span>{{ $t('cms.pages.isFeatured') }}</span>
            </label>
          </cms-sidebar-section>

          <cms-sidebar-section :title="$t('cms.pages.mediaSection')">
            <template v-if="pageId">
              <cms-image-upload
                ref="featuredImageUploadRef"
                v-model="form.featured_image"
                :label="$t('cms.pages.featuredImage')"
                :help-text="$t('cms.pages.featuredImageHelp')"
                :initial-media="featuredUploaderInitialMedia"
                @uploaded="handleFeaturedUploaded"
                @removed="handleFeaturedRemoved"
              />
              <div class="cms-media-actions" :class="{ 'is-media-saving': isSyncingFeaturedMedia }">
                <cms-button
                  variant="secondary"
                  size="sm"
                  :disabled="!canSaveFeaturedMedia || isSyncingFeaturedMedia"
                  @click="saveFeaturedMedia"
                >
                  <v-icon icon="upload" size="sm" />
                  {{ isSyncingFeaturedMedia ? $t('general.button.saving') : $t('cms.pages.uploadFeatured') }}
                </cms-button>
              </div>

              <div class="cms-sidebar-featured-alt">
                <cms-input
                  v-model="getTranslation(currentLanguage).featured_image_alt"
                  :label="`${$t('cms.pages.featuredImageAlt')} (${currentLanguage.toUpperCase()})`"
                  :help-text="$t('cms.pages.featuredImageAltHelp')"
                  :full-width="true"
                  :disabled="isSaving || isLoadingPage"
                  :show-tooltip-on-hover="false"
                />
              </div>
            </template>
            <template v-else>
              <p class="cms-form-help cms-media-disabled">
                {{ $t('cms.activities.mediaUploadRequiresCreation') }}
              </p>
            </template>
          </cms-sidebar-section>

          <cms-sidebar-section :title="$t('cms.pages.relationshipsSection')">
            <label class="cms-input-label">{{ $t('cms.pages.relatedActivities') }}</label>
            <v-select
              v-model="form.related_activities"
              :options="activityOptions"
              multiple
              label="label"
              :reduce="reduceActivityOption"
              class="cms-relationship-select"
              :close-on-select="false"
              :clearable="false"
            />
            <p class="cms-form-help">{{ $t('cms.pages.relatedActivitiesHelp') }}</p>
          </cms-sidebar-section>
        </cms-sidebar>
      </template>
    </cms-form-layout>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import type { PageForm, PageTranslation } from '@/types/cms';
import {
  CmsFormHeader,
  CmsFormLayout,
  CmsFormSection,
  CmsInput,
  CmsTextarea,
  CmsSelect,
  CmsButton,
  CmsEditor,
  CmsSidebar,
  CmsSidebarSection,
  CmsTranslationTabs,
  CmsListInput,
  CmsImageUpload,
} from '@/components/cms';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';
import { hasPendingCmsMediaSave, persistCmsMediaAttachment } from '@/helpers/cmsMediaUpload';
import { runCmsMediaSync } from '@/helpers/cmsMediaSaveFlow';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';

interface SelectOption {
  value: number | string;
  label: string;
}

export default defineComponent({
  name: 'TheCmsPageForm',
  components: {
    CmsFormHeader,
    CmsFormLayout,
    CmsFormSection,
    CmsInput,
    CmsTextarea,
    CmsSelect,
    CmsButton,
    CmsEditor,
    CmsSidebar,
    CmsSidebarSection,
    CmsTranslationTabs,
    CmsListInput,
    CmsImageUpload,
    vSelect,
    VIcon,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const pageId = ref(route.params.id ? Number(route.params.id) : null);
    const isEditing = computed(() => pageId.value !== null);

    const { languages, locales, resolveStoredLocale } = useAppLanguages();
    const currentLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));

    const createEmptyTranslation = (locale: string): PageTranslation => ({
      locale,
      title: '',
      content: '',
      seo_title: '',
      seo_description: '',
      keywords: [],
      featured_image_alt: '',
    });

    const form = reactive<PageForm>({
      custom_url: '',
      page_type: 'custom',
      is_public: false,
      is_featured: false,
      order: 0,
      related_activities: [],
      featured_image: '',
      translations: locales.value.map((locale) => createEmptyTranslation(locale)),
    });

    const activities = ref<SelectOption[]>([]);
    const activityOptions = computed(() => activities.value);

    const pageTypeOptions = computed(() => [
      { value: 'custom', label: t('cms.pages.pageTypeOptions.custom') },
      { value: 'about', label: t('cms.pages.pageTypeOptions.about') },
      { value: 'terms', label: t('cms.pages.pageTypeOptions.terms') },
      { value: 'privacy', label: t('cms.pages.pageTypeOptions.privacy') },
    ]);

    const getTranslation = (locale: string): PageTranslation => {
      let translation = form.translations.find((entry) => entry.locale === locale);
      if (!translation) {
        translation = createEmptyTranslation(locale);
        form.translations.push(translation);
      }
      return translation;
    };

    const stripHtmlContent = (value: string | null | undefined) => {
      if (!value) {
        return '';
      }
      return value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    };

    const requiredTranslationFields: Array<keyof PageTranslation> = ['title', 'content'];

    const translationsAreComplete = computed(() => {
      return form.translations.some((translation) =>
        requiredTranslationFields.every((field) => {
          const value = translation[field];
          if (typeof value === 'string') {
            const normalized = field === 'content' ? stripHtmlContent(value) : value.trim();
            return normalized.length > 0;
          }
          return false;
        }),
      );
    });

    const validateTranslations = () => {
      if (translationsAreComplete.value) {
        return true;
      }
      const missingFields = requiredTranslationFields
        .map((field) => t(`cms.pages.${field === 'title' ? 'pageTitle' : field}`))
        .join(', ');
      toast.error(
        t('cms.stories.translationValidationError', {
          fields: missingFields,
          language: t('cms.stories.translationGenericLanguage'),
        }),
      );
      return false;
    };

    const isSaving = ref(false);
    const isLoadingPage = ref(false);

    const extractListData = (response: any) => {
      if (!response) return [];
      const candidates = [response?.data?.data, response?.data, response?.data?.data?.data, response];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const extractPageData = (response: any) => {
      if (!response) return null;
      const candidates = [response?.data?.data, response?.data, response];
      for (const candidate of candidates) {
        if (!candidate) continue;
        if (candidate.attributes && typeof candidate.attributes === 'object') {
          return {
            id: candidate.id ?? candidate.attributes.id ?? null,
            ...candidate.attributes,
            relationships: candidate.relationships,
          };
        }
        if (candidate.id || candidate.translations || candidate.media) {
          return candidate;
        }
      }
      return null;
    };

    const mapPageToForm = (page: any) => {
      form.custom_url = page.customUrl || page.custom_url || '';
      form.page_type = page.pageType || page.page_type || 'custom';
      form.is_public = !!(page.isPublic ?? page.is_public);
      form.is_featured = !!(page.isFeatured ?? page.is_featured);
      form.order = page.order || 0;

      const relatedActivities = extractRelationship(page, 'relatedActivities');
      const normalized = normalizeResource(relatedActivities);
      if (Array.isArray(normalized)) {
        form.related_activities = normalized
          .map((activity: any) => activity?.id)
          .filter((id: any): id is number => typeof id === 'number');
      } else if (Array.isArray(page.relatedActivities)) {
        form.related_activities = page.relatedActivities
          .map((activity: any) => activity?.id)
          .filter((id: any): id is number => typeof id === 'number');
      } else {
        form.related_activities = [];
      }
    };

    const applyTranslationsFromPage = (page: any, locale: string) => {
      const translations = extractRelationship(page, 'translations');
      const normalized = normalizeResource(translations);
      const translation = Array.isArray(normalized) ? normalized[0] : normalized || page.translations?.[0];
      if (!translation) {
        return;
      }
      const target = getTranslation(locale);
      target.title = translation.title || '';
      target.content = translation.content || '';
      target.seo_title = translation.seo_title || '';
      target.seo_description = translation.seo_description || '';
      target.keywords = Array.isArray(translation.keywords)
        ? translation.keywords
        : translation.keywords
          ? [translation.keywords]
          : [];
      target.featured_image_alt = translation.featured_image_alt ?? translation.featuredImageAlt ?? '';
    };

    const buildPayload = () => {
      const translationsPayload = form.translations
        .map((translation) => ({
          locale: translation.locale,
          title: translation.title?.trim(),
          content: translation.content,
          seo_title: translation.seo_title,
          seo_description: translation.seo_description,
          keywords: translation.keywords.filter((keyword) => !!keyword?.trim()),
          featured_image_alt: translation.featured_image_alt?.trim() || '',
        }))
        .filter((translation) =>
          requiredTranslationFields.every((field) => {
            const value = translation[field];
            if (typeof value === 'string') {
              const normalized = field === 'content' ? stripHtmlContent(value) : value.trim();
              return normalized.length > 0;
            }
            return false;
          }),
        );

      return {
        custom_url: form.custom_url.trim(),
        page_type: form.page_type,
        is_public: form.is_public,
        is_featured: form.is_featured,
        order: form.order,
        related_activities: form.related_activities,
        translations: translationsPayload,
      };
    };

    const extractPageId = (response: any): number | null => {
      if (!response) return null;
      if (typeof response.id === 'number') {
        return response.id;
      }
      if (typeof response?.data?.id === 'number') {
        return response.data.id;
      }
      if (typeof response?.data?.data?.id === 'number') {
        return response.data.data.id;
      }
      return null;
    };

    const loadActivities = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.activityService.listCmsActivities(agencySlug.value, {
          perPage: 100,
          order: { key: 'updated_at', direction: 'desc' },
        });
        const list = extractListData(response);
        activities.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          const translations = extractRelationship(normalized, 'translations');
          const translation = Array.isArray(translations) ? translations[0] : translations;
          return {
            value: normalized.id,
            label: translation?.title || normalized.title || normalized.name || `#${normalized.id}`,
          };
        });
      } catch (error) {
        console.error('Failed to load activities', error);
      }
    };

    const featuredImageUploadRef = ref<any>(null);
    const featuredUploaderInitialMedia = ref<any[]>([]);
    const featuredMediaPayload = ref<any[] | null>(null);
    const pendingFeaturedDeletion = ref<any | null>(null);
    const isSyncingFeaturedMedia = ref(false);

    const CMS_MEDIA_MODEL_TYPE = 'cms_page';
    const FEATURED_MEDIA_COLLECTION = 'cms_page_featured';
    const MEDIA_DRIVER = 's3';

    const cloneMediaItems = (items: any[] = []) => items.map((item) => JSON.parse(JSON.stringify(item)));

    const getCollectionName = (item: any) => item?.collection_name || item?.collectionName || '';
    const getCustomProperties = (item: any) => item?.custom_properties || item?.customProperties || {};
    const getOrderColumn = (item: any) => item?.order_column ?? item?.orderColumn;

    const getMediaUrl = (item: any) => {
      if (!item) return '';
      const attributes = item.attributes || {};
      return (
        item.storage_url ||
        item.storageUrl ||
        attributes.storage_url ||
        attributes.storageUrl ||
        item.original_url ||
        item.originalUrl ||
        attributes.original_url ||
        attributes.originalUrl ||
        item.preview_url ||
        attributes.preview_url ||
        ''
      );
    };

    const getMediaPreviewUrl = (item: any, fallback: string) => {
      const attributes = item?.attributes || {};
      return (
        item.preview_url ||
        item.previewUrl ||
        attributes.preview_url ||
        attributes.previewUrl ||
        fallback
      );
    };

    const normalizeMediaForUploader = (item: any, fallbackOrder = 1) => {
      if (!item) return null;
      const customProps = getCustomProperties(item);
      const order = customProps?.order ?? getOrderColumn(item) ?? fallbackOrder;
      const storageUrl = getMediaUrl(item);
      const previewUrl = getMediaPreviewUrl(item, storageUrl);

      return {
        ...item,
        order,
        order_column: order,
        original_url: storageUrl,
        storage_url: storageUrl,
        preview_url: previewUrl,
        attributes: {
          ...(item.attributes || {}),
          uuid: item.uuid,
          name: item.name || `media-${order}`,
          order_column: order,
          collection_name: getCollectionName(item),
          original_url: storageUrl,
          preview_url: previewUrl,
          isExisting: true,
          urls: {
            ...(item.attributes?.urls || {}),
            original: storageUrl,
            preview: previewUrl,
          },
        },
      };
    };

    const extractMediaItems = (page: any) => {
      const mediaRelationship = extractRelationship(page, 'media');
      const normalizedRelationship = normalizeResource(mediaRelationship);
      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }
      if (Array.isArray(page.media)) {
        return page.media;
      }
      return [];
    };

    const mapPageMediaCollections = (page: any) => {
      const mediaItems = extractMediaItems(page)
        .map((item: any) => normalizeResource(item) || item)
        .filter((item: any) => !!item && (item.uuid || item.id));

      const featuredItems = mediaItems
        .filter((item: any) => getCollectionName(item) === FEATURED_MEDIA_COLLECTION)
        .map((item: any, index: number) => normalizeMediaForUploader(item, index + 1))
        .filter(Boolean);

      featuredUploaderInitialMedia.value = cloneMediaItems(featuredItems);
      pendingFeaturedDeletion.value = null;

      const featuredUrl = getMediaUrl(featuredItems[0]);
      form.featured_image = featuredUrl || '';
    };

    const deleteMediaItemsByIds = async (ids: Array<number | null | undefined>) => {
      const uniqueIds = Array.from(
        new Set((ids || []).filter((id): id is number => typeof id === 'number' && id > 0)),
      );
      if (!uniqueIds.length) {
        return;
      }
      await Promise.all(uniqueIds.map((id) => api.media.delete(MEDIA_DRIVER, id)));
    };

    const buildMediaConfirmEntries = (mediaItems: any[] = [], collection: string) => {
      return (Array.isArray(mediaItems) ? mediaItems : [])
        .map((item: any, index: number) => {
          const attributes = item?.attributes ?? {};
          if (attributes.isExisting) {
            return null;
          }
          const uuid = attributes.uuid ?? item.uuid;
          if (!uuid) {
            return null;
          }
          const order = attributes.order ?? attributes.order_column ?? item.order ?? index + 1;
          return {
            uuid,
            name: attributes.name ?? item.name ?? `media-${order}`,
            order,
            collection,
          };
        })
        .filter((entry): entry is { uuid: string; name: string; order: number; collection: string } => !!entry);
    };

    const refreshPageMediaState = async () => {
      if (!agencySlug.value || !pageId.value) {
        return;
      }
      try {
        const response = await api.pageService.getCmsPage(agencySlug.value, pageId.value);
        const pageData = extractPageData(response);
        if (pageData) {
          mapPageMediaCollections(pageData);
        }
      } catch (error) {
        console.error('Error refreshing page media', error);
      }
    };

    const confirmFeaturedMedia = async () => {
      if (!pageId.value || isSyncingFeaturedMedia.value || isLoadingPage.value) {
        return;
      }
      const payload = featuredMediaPayload.value ?? [];
      const hasUploads = hasPendingCmsMediaSave(payload, FEATURED_MEDIA_COLLECTION);
      const hasPendingDeletion = !!pendingFeaturedDeletion.value?.id;
      if (!hasUploads && !hasPendingDeletion) {
        return;
      }
      try {
        await runCmsMediaSync(isSyncingFeaturedMedia, async () => {
          if (hasPendingDeletion) {
            await deleteMediaItemsByIds([pendingFeaturedDeletion.value.id]);
            pendingFeaturedDeletion.value = null;
          } else if (hasUploads && featuredUploaderInitialMedia.value.length > 0) {
            await deleteMediaItemsByIds(featuredUploaderInitialMedia.value.map((item: any) => item.id));
          }
          if (hasUploads) {
            await persistCmsMediaAttachment({
              api,
              driver: MEDIA_DRIVER,
              agencySlug: agencySlug.value!,
              mediaItems: payload,
              modelType: CMS_MEDIA_MODEL_TYPE,
              modelId: pageId.value,
              collection: FEATURED_MEDIA_COLLECTION,
            });
          }
          featuredMediaPayload.value = null;
          featuredImageUploadRef.value?.clearMedia?.();
          await refreshPageMediaState();
        });
        toast.success(t('cms.activities.featuredMediaSaveSuccess'));
      } catch (error) {
        console.error('Error saving featured media', error);
        toast.error(t('general.errorMessage'));
      }
    };

    const persistPendingMediaChanges = async () => {
      await confirmFeaturedMedia();
    };

    const handleFeaturedUploaded = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };

    const handleFeaturedRemoved = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
      if ((!media || media.length === 0) && featuredUploaderInitialMedia.value.length > 0) {
        pendingFeaturedDeletion.value = featuredUploaderInitialMedia.value[0];
      }
    };

    const canSaveFeaturedMedia = computed(
      () =>
        !!pageId.value &&
        featuredMediaPayload.value !== null &&
        !isSyncingFeaturedMedia.value &&
        !isSaving.value &&
        !isLoadingPage.value,
    );

    const saveFeaturedMedia = async () => {
      await confirmFeaturedMedia();
    };

    const loadPage = async () => {
      if (!agencySlug.value || !pageId.value) {
        return;
      }
      isLoadingPage.value = true;
      try {
        for (const locale of locales.value) {
          const response = await api.pageService.getCmsPage(agencySlug.value, pageId.value, {
            headers: {
              'Accept-Language': locale,
            },
          });
          const pageData = extractPageData(response);
          if (!pageData) {
            continue;
          }
          if (locale === locales.value[0]) {
            mapPageToForm(pageData);
            mapPageMediaCollections(pageData);
          }
          applyTranslationsFromPage(pageData, locale);
        }
      } catch (error) {
        console.error('Failed to load page', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isLoadingPage.value = false;
      }
    };

    const persistPage = async (publish = false) => {
      if (!agencySlug.value) return;
      if (!validateTranslations()) {
        return;
      }
      const payload = buildPayload();
      isSaving.value = true;
      try {
        if (pageId.value) {
          await api.pageService.updateCmsPage(agencySlug.value, pageId.value, payload);
        } else {
          const response = await api.pageService.createCmsPage(agencySlug.value, payload);
          const newId = extractPageId(response);
          if (newId) {
            pageId.value = newId;
            try {
              await router.replace({ name: 'cms.pages.edit', params: { id: newId } });
            } catch (error) {
              console.warn('Router replace failed', error);
            }
            await loadPage();
          }
        }
        await persistPendingMediaChanges();
        if (publish && pageId.value) {
          await api.pageService.publishCmsPage(agencySlug.value, pageId.value, {
            is_public: true,
          });
          form.is_public = true;
        }
        toast.success(publish ? t('cms.pages.publishSuccess') : t('general.shared.savedChanges'));
      } catch (error: any) {
        console.error('Error saving page', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isSaving.value = false;
      }
    };

    const reduceActivityOption = (option: SelectOption) => option.value;

    const saveDraft = () => {
      form.is_public = false;
      persistPage(false);
    };

    const saveAndPublish = () => {
      form.is_public = true;
      persistPage(true);
    };

    const handleSubmit = () => {
      saveDraft();
    };

    const goBack = () => {
      router.push({ name: 'cms.pages' });
    };

    watch(
      () => route.params.id,
      (newId) => {
        pageId.value = newId ? Number(newId) : null;
        if (pageId.value) {
          loadPage();
        } else {
          form.custom_url = '';
          form.page_type = 'custom';
          form.is_public = false;
          form.is_featured = false;
          form.order = 0;
          form.related_activities = [];
          form.featured_image = '';
          form.translations = locales.value.map((locale) => createEmptyTranslation(locale));
          featuredUploaderInitialMedia.value = [];
        }
      },
      { immediate: true },
    );

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          loadActivities();
          if (pageId.value) {
            loadPage();
          }
        }
      },
      { immediate: true },
    );

    return {
      t,
      form,
      isEditing,
      isSaving,
      isLoadingPage,
      languages,
      currentLanguage,
      getTranslation,
      pageTypeOptions,
      activityOptions,
      reduceActivityOption,
      featuredImageUploadRef,
      featuredUploaderInitialMedia,
      canSaveFeaturedMedia,
      saveFeaturedMedia,
      handleFeaturedUploaded,
      handleFeaturedRemoved,
      saveDraft,
      saveAndPublish,
      handleSubmit,
      goBack,
      pageId,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-page-form {
  color: $cms-text-primary;
}

.cms-media-actions {
  margin-top: $cms-spacing-xs;
  display: flex;
  justify-content: flex-end;
}

.cms-media-disabled {
  color: $cms-text-secondary;
}

.cms-relationship-select {
  width: 100%;
}
</style>

